import { HttpBackend, HttpEvent, HttpClient, HttpErrorResponse, HttpHeaders, HttpParameterCodec, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NotifyUserService } from '@shared/notify-user.service';
import { environment } from 'environments/environment';
import { BehaviorSubject, EMPTY, Observable, defer, of, throwError, timer } from 'rxjs';
import { catchError, concatMap, filter, finalize, map, mapTo, retryWhen, share, switchMap } from 'rxjs/operators';
import { AccountData, AuthData, DecodedJWTToken, LoginResponseJWT, RefreshTokenRequest } from './authentication.models';

export class LoginVM {
    email: string;
    password: string;
    rememberMe: boolean;
}

export class CustomEncoder implements HttpParameterCodec {
    encodeKey(key: string): string {
        return encodeURIComponent(key);
    }

    encodeValue(value: string): string {
        return encodeURIComponent(value);
    }

    decodeKey(key: string): string {
        return decodeURIComponent(key);
    }

    decodeValue(value: string): string {
        return decodeURIComponent(value);
    }
}

/**
 * Backoff (ms) used when a proactive, transiently-failed token refresh is retried.
 * Kept short on purpose: the access token still has the exp-5min cushion and the
 * reactive 401 interceptor remains the safety net once it actually expires.
 */
const PROACTIVE_REFRESH_RETRY_DELAYS_MS = [5000, 15000, 45000];

@Injectable({
    providedIn: 'root',
})
export class AuthenticationService {
    // debugNeedRefresh(){
    //     this._accountData.next({NeedsRefresh:true, Ruoli:['StudioUser']});
    // }

    get user() {
        return this._user;
    }
    get accountData$() {
        return this._accountData.asObservable();
    }

    get decodedToken$() {
        return this._decodedToken.asObservable();
    }
    get decodedToken() {
        return this._decodedToken.value;
    }

    get authData$() {
        return this._authData.asObservable();
    }
    get authData() {
        return this._authData.value;
    }

    get isAuthenticated$() {
        return this._authData.asObservable().pipe(map(authData => authData !== null));
    }
    get isAuthenticated() {
        return this._authData.value !== null;
    }
    get isLoading$() {
        return this._isLoading.asObservable();
    }

    get serviceError$() {
        return this._serviceError.asObservable();
    }

    /**
     * Returns the current in-memory access token so consumers (e.g. the SignalR
     * accessTokenFactory) always read the LIVE token, including after a silent refresh.
     * Returns null when no session is loaded (the backing field is initialized to null).
     */
    getAccessToken(): string | null {
        return this._accessToken;
    }

    constructor(
        private http: HttpClient,
        private httpBackend: HttpBackend,
        private notifyUserService: NotifyUserService
    ) {
        this.loadTokenAndUserFromStorage();
    }

    private _authData = new BehaviorSubject<AuthData>(null);
    private _accountData = new BehaviorSubject<AccountData>(null);
    private _serviceError = new BehaviorSubject<any>(null);
    private _decodedToken = new BehaviorSubject<DecodedJWTToken>(null);
    private _accessToken: string | null = null;
    private _refreshToken: string = null;
    private _user: string = null;

    private _isLoading = new BehaviorSubject<boolean>(false);

    // Single-flight refresh coordination
    private refreshInProgress$: Observable<string> | null = null;

    // Identifies the current auth session epoch. Incremented on every session transition
    // (successful login, logout) so an in-flight refresh can detect it has become stale.
    private _sessionGeneration = 0;

    setDecodedToken(dT: DecodedJWTToken) {
        this._decodedToken.next(dT);
    }

    getAuthTokenOrDefault() {
        if (this.authData) {
            return this.authData;
        } else {
            return null;
        }
    }

    loadTokenAndUserFromStorage() {
        try {
            const accessToken: string = localStorage.getItem('access-token');
            const refreshToken: string = localStorage.getItem('refresh-token');

            if (accessToken != null) {
                // Migration: existing sessions without refresh token must re-login
                if (refreshToken == null) {
                    localStorage.clear();
                    window.location.reload();
                    return;
                }

                this._accessToken = accessToken;
                this._refreshToken = refreshToken;
                this._user = localStorage.getItem('user');
                const decodedToken: DecodedJWTToken = new JwtHelperService().decodeToken(accessToken);

                const auth: AuthData = {
                    accessToken,
                    refreshToken,
                    utentiId: Number(decodedToken.Uid),
                };

                this._authData.next(auth);
            }
        } catch {
            localStorage.clear();
            // const description = { exception: '', message: 'Autenticazione non valida' };
            // const errorCode = { code: '500', description: description, internalErrors: [], showModal: false };
            // this.notifyUserService.showError(errorCode);
            window.location.reload();
        }
    }

    saveTokenToStorage() {
        localStorage.setItem('access-token', this._accessToken);
        localStorage.setItem('refresh-token', this._refreshToken);
        localStorage.setItem('user', this._user);
    }

    login(loginVM: LoginVM) {
        const urlencodedHeaders = new HttpHeaders().set('Content-Type', 'application/json');

        this._isLoading.next(true);
        return this.http
            .post<LoginResponseJWT>(
                `${environment.coreApiUrl}/auth/login`,
                // The device uuid binds the refresh token to this browser at login time
                // (per-device row server-side): without it the session depends on the
                // shared legacy slot and any other client login/refresh can invalidate it.
                { email: loginVM.email, password: loginVM.password, uuid: this.getUuid() },
                {
                    headers: urlencodedHeaders,
                }
            )
            .pipe(
                map(loginResult => {
                    // A successful login starts a new session epoch, invalidating any refresh
                    // that may still be in flight from the previous session.
                    this._sessionGeneration++;

                    const helper = new JwtHelperService();
                    const decodedToken: DecodedJWTToken = helper.decodeToken(loginResult.accessToken);

                    this.setDecodedToken(decodedToken);
                    const authData: AuthData = {
                        accessToken: loginResult.accessToken,
                        refreshToken: loginResult.refreshToken,
                        utentiId: Number(decodedToken.Uid),
                    };

                    this._accessToken = loginResult.accessToken;
                    this._refreshToken = loginResult.refreshToken;

                    this._user = decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'];

                    if (loginVM.rememberMe === true) {
                        this.saveTokenToStorage();
                    }
                    this._authData.next(authData);
                    return loginResult;
                }),
                catchError((error: HttpErrorResponse) => {
                    if (error.status.toString().startsWith('5')) this.notifyUserService.showErrorModal({ erroreHtml: 'Errore sconosciuto', messaggi: [] });

                    return throwError(error);
                }),
                finalize(() => {
                    this._isLoading.next(false);
                })
            );
    }

    logout() {
        // Invalidate any refresh request that is currently in flight.
        this._sessionGeneration++;
        localStorage.clear();
        this._authData.next(null);
        this._decodedToken.next(null);
        this._accessToken = null;
        this._refreshToken = null;
        this._user = null;
        this.refreshInProgress$ = null;
    }

    forgotPassword(email: string): Observable<any> {
        this._isLoading.next(true);
        return this.http.post(`${environment.coreApiUrl}/auth/forgot-password`, { email }).pipe(
            map(() => {}),
            catchError(error => {
                if (error) {
                    return throwError(error);
                } else {
                    return throwError('error');
                }
            }),

            finalize(() => {
                this._isLoading.next(false);
            })
        );
    }

    checkPassword(password: string): Observable<any> {
        return this.login({ email: this._user, password, rememberMe: false }).pipe(
            mapTo(true),
            catchError(() => throwError(false))
        );
    }

    resetPassword(resetPayload): Observable<any> {
        this._isLoading.next(true);
        return this.http.post(`${environment.coreApiUrl}/auth/reset-password`, resetPayload).pipe(
            map(() => {}),
            catchError(error => {
                if (error) {
                    return throwError(error);
                } else {
                    return throwError('error');
                }
            }),

            finalize(() => {
                this._isLoading.next(false);
            })
        );
    }

    tokenIsExpiring() {
        // The access token is within 5 minutes of expiry: refresh it silently so the user
        // never notices. A failure caused by a flaky network (offline, 5xx, timeout) must
        // NOT end the session: retry a few times with backoff and, if the backend stays
        // unreachable, just warn. Only an explicit rejection of the refresh token
        // (400/401/403, handled in handleTokenRefresh) ends the session; in that case
        // AppComponent reacts to authData$ === null and redirects to login.
        let retryIndex = 0;

        // defer() makes every retry re-enter handleTokenRefresh(), so the retries keep
        // sharing the single-flight coordinator with the interceptors instead of
        // re-subscribing to a stale, already-failed shared observable.
        defer(() => this.handleTokenRefresh())
            .pipe(
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error: unknown) => {
                            const canRetry =
                                this.isTransientRefreshFailure(error) &&
                                retryIndex < PROACTIVE_REFRESH_RETRY_DELAYS_MS.length;

                            if (!canRetry) {
                                return throwError(error);
                            }

                            return timer(PROACTIVE_REFRESH_RETRY_DELAYS_MS[retryIndex++]);
                        })
                    )
                ),
                catchError((error: unknown) => {
                    if (this.isTransientRefreshFailure(error)) {
                        this.notifyUserService.showWarn(
                            'Connessione instabile: non è stato possibile rinnovare la sessione. Riproveremo automaticamente.'
                        );
                    }
                    // Swallow the failure: on a definitive one handleTokenRefresh() already
                    // logged out, so returning EMPTY avoids a redundant, unhandled global error.
                    return EMPTY;
                })
            )
            .subscribe();
    }

    /**
     * True when the refresh failed for a reason that ends the session for good: the
     * backend explicitly rejected the refresh token (400/401/403) or an unexpected,
     * non-HTTP error occurred. Retrying such failures is pointless.
     */
    private isDefinitiveRefreshFailure(error: unknown): boolean {
        if (error instanceof HttpErrorResponse) {
            return error.status === 400 || error.status === 401 || error.status === 403;
        }

        // Non-HTTP errors ("No tokens available", programming errors) are not network
        // hiccups: fail safe and treat them as definitive.
        return true;
    }

    /**
     * True for failures that leave the session recoverable: offline (status 0), 5xx,
     * timeouts and the like. In these cases the tokens stay valid and we can retry.
     */
    private isTransientRefreshFailure(error: unknown): boolean {
        return !this.isDefinitiveRefreshFailure(error);
    }

    /**
     * Client identity sent in the /auth/refresh body. Generated on first use with
     * crypto.randomUUID() and persisted in localStorage; reused across sessions
     * until logout() wipes storage, after which a new identity is generated.
     */
    private getUuid(): string {
        let uuid = localStorage.getItem('uuid');
        if (!uuid) {
            // This project's TypeScript DOM lib predates Crypto.randomUUID: a structural
            // view keeps the call type-safe without altering runtime behavior.
            uuid = (crypto as Crypto & { randomUUID: () => string }).randomUUID();
            localStorage.setItem('uuid', uuid);
        }
        return uuid;
    }

    /**
     * Calls the /auth/refresh endpoint using HttpBackend to bypass interceptors.
     * Returns the new access token string on success.
     */
    refreshAccessToken(): Observable<string> {
        // Capture the session epoch before the request so a late response can be discarded
        // if the session was terminated or replaced while the refresh was in flight.
        const generation = this._sessionGeneration;

        const url = `${environment.coreApiUrl}/auth/refresh`;
        const body: RefreshTokenRequest = {
            accessToken: this._accessToken,
            refreshToken: this._refreshToken,
            uuid: this.getUuid(),
        };
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Authorization', `Bearer ${this._accessToken}`);

        return this.httpBackend.handle(
            new HttpRequest('POST', url, body, { headers })
        ).pipe(
            filter((event: HttpEvent<LoginResponseJWT>) => event instanceof HttpResponse),
            switchMap((event: HttpResponse<LoginResponseJWT>): Observable<string> => {
                // Discard a stale refresh: a logout() (or a new login()) happened while this
                // request was in flight, so applying it would resurrect a terminated session.
                if (generation !== this._sessionGeneration) {
                    return EMPTY;
                }

                const response = event.body;

                // Update internal state with new tokens
                this._accessToken = response.accessToken;
                this._refreshToken = response.refreshToken;
                this._user = response.email;

                // Decode and store new token
                const helper = new JwtHelperService();
                const decodedToken: DecodedJWTToken = helper.decodeToken(response.accessToken);
                this.setDecodedToken(decodedToken);

                // Update AuthData
                const authData: AuthData = {
                    accessToken: response.accessToken,
                    refreshToken: response.refreshToken,
                    utentiId: Number(decodedToken.Uid),
                };
                this._authData.next(authData);

                // Persist if tokens were previously stored (rememberMe was true)
                if (localStorage.getItem('access-token') !== null) {
                    this.saveTokenToStorage();
                }

                return of(response.accessToken);
            }),
            catchError((error: HttpErrorResponse) => {
                console.error('[AuthenticationService] Refresh token failed:', error);
                return throwError(error);
            })
        );
    }

    /**
     * Orchestrates a single-flight token refresh.
     * If a refresh is already in progress, returns the same observable so all
     * callers share the result (success or error).
     */
    handleTokenRefresh(): Observable<string> {
        // Prevent refresh attempt if no tokens are available (e.g., after logout)
        if (!this._accessToken || !this._refreshToken) {
            return throwError(new Error('No tokens available for refresh'));
        }

        if (!this.refreshInProgress$) {
            this.refreshInProgress$ = this.refreshAccessToken().pipe(
                catchError((error: HttpErrorResponse) => {
                    if (this.isDefinitiveRefreshFailure(error)) {
                        // The refresh token is no longer accepted: the session cannot be
                        // recovered, so wipe it. AppComponent reacts to authData$ === null
                        // and redirects to login.
                        this.logout();
                    } else {
                        // Transient failure (offline, 5xx, timeout): the session is still
                        // valid. Keep it; the proactive timer retries with backoff and the
                        // reactive 401 interceptor remains the safety net.
                        console.warn('[AuthenticationService] Transient token refresh failure — session kept alive', error);
                    }
                    return throwError(error);
                }),
                // Always clear the single-flight reference: on success, error or unsubscribe,
                // and only after the value has been delivered downstream.
                finalize(() => {
                    this.refreshInProgress$ = null;
                }),
                share()
            );
        }
        return this.refreshInProgress$;
    }
}
